import axios from 'axios';
import { Device } from '../types';

export interface LoggingModeOption {
  value: number;
  label: string;      // Full label with the numeric level, e.g. "1 - Errors"
  shortLabel: string; // Just the name, e.g. "Errors"
}

// Shared logging levels used for both devices and users, so the mobile app
// receives a consistent loggingMode int (0 = none ... 4 = debug).
export const LOGGING_MODE_OPTIONS: LoggingModeOption[] = [
  { value: 0, label: '0 - No Logs', shortLabel: 'No Logs' },
  { value: 1, label: '1 - Errors', shortLabel: 'Errors' },
  { value: 2, label: '2 - Warnings', shortLabel: 'Warnings' },
  { value: 3, label: '3 - Info', shortLabel: 'Info' },
  { value: 4, label: '4 - Debug', shortLabel: 'Debug' },
];

export const DEFAULT_LOGGING_MODE = 1;

export function loggingModeLabel(mode: number): string {
  return LOGGING_MODE_OPTIONS.find((option) => option.value === mode)?.label ?? String(mode);
}

export function loggingModeShortLabel(mode: number): string {
  return LOGGING_MODE_OPTIONS.find((option) => option.value === mode)?.shortLabel ?? String(mode);
}

// Badge background colors by severity: grey -> darker grey -> yellow -> orange -> red.
const LOGGING_MODE_COLORS: Record<number, string> = {
  0: '#6c757d', // No Logs - grey
  1: '#495057', // Errors - darker grey
  2: '#ffc107', // Warnings - yellow
  3: '#fd7e14', // Info - orange
  4: '#dc3545', // Debug - red
};

export function loggingModeBadgeStyle(mode: number): { backgroundColor: string; color: string } {
  return { backgroundColor: LOGGING_MODE_COLORS[mode] ?? '#6c757d', color: '#fff' };
}

export type UpdateDeviceLoggingModeResult = {
  device: Device;
  mqttCommand?: string;
};

/** Persist logging mode and push the matching MQTT command to the device. */
export async function updateDeviceLoggingMode(
  apiBaseUrl: string,
  serial: string,
  mode: number,
): Promise<UpdateDeviceLoggingModeResult> {
  const response = await axios.patch(
    `${apiBaseUrl}/devices/${encodeURIComponent(serial)}/logging-mode`,
    { loggingMode: mode },
    { withCredentials: true },
  );

  return response.data;
}

export function getLoggingModeErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Failed to update logging mode';
}
